import React, { FC, useContext } from "react";
import { AddressElement, PaymentElement } from "@stripe/react-stripe-js";
import { LocalizationContext } from "v3/context/localization";
import { Checkbox } from "@pepdirect/v3/ui/Checkbox";
import { AddressDetails } from "v3/components/orderDetails/AddressDetails";
import {
  StyledCheckboxContainer,
  StyledLabel,
  StyledTitle,
  StyledAlert,
} from "./index.styled";
import {
  AllowedPaymentMethod,
  CheckoutSessionFragment,
} from "v3/services/graphql/generated";
import { PaymentContext } from "v3/context/payment";

interface StripeProps {
  contactInfo: CheckoutSessionFragment["contactInfo"];
  isBillingEqualShipping: boolean;
  setIsBillingEqualShipping: (isBillingEqualShipping: boolean) => void;
  orderHasSubscription: boolean;
  saveCardForLater: boolean;
  setSaveCardForLater: (value: boolean) => void;
  handleChangePaymentType: (value: string) => void;
  orderHasGasExchangeItems: boolean;
  isGuest: boolean;
}

export const Stripe: FC<StripeProps> = ({
  contactInfo,
  isBillingEqualShipping,
  setIsBillingEqualShipping,
  orderHasSubscription,
  saveCardForLater,
  setSaveCardForLater,
  handleChangePaymentType,
  orderHasGasExchangeItems,
  isGuest,
}) => {
  const { paymentType } = useContext(PaymentContext);
  const { localization } = useContext(LocalizationContext);
  const {
    billingSameAsShippingText = "Billing address is the same as shipping address",
    shippingAddressHeadingText = "Shipping Address",
    billingAddressHeadingText = "Billing Address",
  } = localization?.checkout?.paymentStep || {};
  const {
    saveCardForLaterText = "Save this card for later use",
    securityNote = "All transactions are secured by 256-bit bank-grade encryption.",
    cardSavedForSubsNote = "This credit card will be saved for future subscription payments",
    cardSavedForGasExchangeNote = "This credit card will be saved for future gas exchange payments",
  } = localization?.forms?.creditCard || {};

  const saveCardForLaterEnabled =
    !orderHasSubscription &&
    !orderHasGasExchangeItems &&
    !isGuest &&
    paymentType === AllowedPaymentMethod.CreditCard;

  const { email, phone, shippingAddress } = contactInfo || {};
  const fullName = `${shippingAddress?.firstName || ""} ${
    shippingAddress?.lastName || ""
  }`.trim();
  const defaultCountry = shippingAddress?.countryCode || "US";

  return (
    <>
      <PaymentElement
        id="payment-element"
        onChange={(event) => handleChangePaymentType(event.value.type)}
        options={{
          layout: "tabs",
          defaultValues: {
            billingDetails: {
              email: email || "",
              name: fullName,
              address: {
                country: defaultCountry,
              },
            },
          },
        }}
      />
      <StyledAlert type={"lock"}>{securityNote}</StyledAlert>
      {(orderHasSubscription || orderHasGasExchangeItems) && (
        <StyledAlert type={"info"}>
          {orderHasSubscription
            ? cardSavedForSubsNote
            : cardSavedForGasExchangeNote}
        </StyledAlert>
      )}
      {saveCardForLaterEnabled && (
        <StyledCheckboxContainer>
          <StyledLabel>
            <Checkbox
              checked={saveCardForLater}
              onChange={() => setSaveCardForLater(!saveCardForLater)}
            />
            {saveCardForLaterText}
          </StyledLabel>
        </StyledCheckboxContainer>
      )}
      <StyledCheckboxContainer>
        <StyledTitle>{billingAddressHeadingText}</StyledTitle>
        <StyledLabel>
          <Checkbox
            checked={isBillingEqualShipping}
            onChange={() => setIsBillingEqualShipping(!isBillingEqualShipping)}
          />
          {billingSameAsShippingText}
        </StyledLabel>
      </StyledCheckboxContainer>

      {isBillingEqualShipping ? (
        <>
          <StyledTitle>{shippingAddressHeadingText}</StyledTitle>
          <AddressDetails address={shippingAddress || null} phone={phone} />
        </>
      ) : (
        // https://docs.stripe.com/payments/accept-a-payment-deferred?platform=web&type=payment#collect-addresses
        <AddressElement
          options={{
            mode: "billing",
            defaultValues: {
              name: fullName,
              address: {
                country: defaultCountry,
              },
            },
          }}
        />
      )}
    </>
  );
};
